import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronRight,
} from '@mui/icons-material';

import { describePkStrategy } from '../../lib/syntheticPk';
import {
  pkFormulaForLevel,
  type SchemaDefinitionLike,
} from '../../lib/schemaTopology';
import type { OutputLevel } from '../../lib/outputStructure';
import ColumnRowWithCatalog from './ColumnRowWithCatalog';

export interface LevelTableViewProps {
  /** Schema definition (used for catalog resolution + PK formulas). */
  definition: SchemaDefinitionLike;
  /** The table being viewed. May be `null` for an empty state. */
  tableName: string | null;
  /** The level the table sits at (if any) — drives the breadcrumb + ID
   *  formula caption. */
  level?: OutputLevel | null;
  /** Breadcrumb of element names from root to the level. */
  breadcrumb?: string[];
  /** Mode. */
  mode: 'view' | 'edit';
  /** Edit-mode mutators. */
  onTablePatch?: (field: string, value: any) => void;
  onColumnPatch?: (colIndex: number, field: string, value: any) => void;
  onAddColumn?: () => void;
  onRemoveColumn?: (colIndex: number) => void;
  onLevelPatch?: (field: keyof OutputLevel, value: any) => void;
  onOpenRulesEditor?: (colIndex: number) => void;
}

/**
 * Right pane of the unified schema browser. Renders the table header
 * (name, breadcrumb, description, ID formula) and the column rows.
 * Columns delegated to `<ColumnRowWithCatalog>`.
 */
const LevelTableView: React.FC<LevelTableViewProps> = ({
  definition,
  tableName,
  level,
  breadcrumb = [],
  mode,
  onTablePatch,
  onColumnPatch,
  onAddColumn,
  onRemoveColumn,
  onLevelPatch,
  onOpenRulesEditor,
}) => {
  const isEdit = mode === 'edit';
  if (!tableName) {
    return (
      <Alert severity="info" variant="outlined">
        Pick a level or table on the left to see its columns.
      </Alert>
    );
  }

  const table = definition.tables?.[tableName];
  if (!table) {
    return (
      <Alert severity="warning" variant="outlined">
        Table <strong>{tableName}</strong> isn't in the schema yet.
      </Alert>
    );
  }

  const description: string = table.description || '';
  const rawColumns = table.columns || {};
  const columnEntries: Array<{ key: string; column: any; index: number }> = Array.isArray(rawColumns)
    ? rawColumns.map((col: any, idx: number) => ({
        key: col?.name || String(idx),
        column: col,
        index: idx,
      }))
    : Object.entries(rawColumns as Record<string, any>).map(([k, v], idx) => ({
        key: k,
        column: { ...(v as any), name: k },
        index: idx,
      }));

  const formula = pkFormulaForLevel(level || null, definition);
  const formulaPhrase = formula ? describePkStrategy(formula.strategy, formula.inputs) : '';
  const columnNames = columnEntries.map((e) => e.column?.name || e.key).filter(Boolean);

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600}>
          {tableName}
        </Typography>
        {breadcrumb.length > 0 && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              at:
            </Typography>
            {breadcrumb.map((b, i) => (
              <React.Fragment key={`${b}-${i}`}>
                {i > 0 && <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />}
                <Typography variant="caption" color="text.secondary">
                  {b}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        )}
        {/* Description */}
        <Box sx={{ mt: 1 }}>
          {isEdit ? (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              placeholder="Table description"
              value={description}
              onChange={(e) => onTablePatch?.('description', e.target.value)}
            />
          ) : description ? (
            <Tooltip
              title={
                description.length > 160 ? (
                  <Box sx={{ maxWidth: 480, whiteSpace: 'pre-wrap' }}>{description}</Box>
                ) : (
                  ''
                )
              }
              disableHoverListener={description.length <= 160}
              placement="bottom-start"
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {description}
              </Typography>
            </Tooltip>
          ) : null}
        </Box>
        {/* Level info: key column dropdown (edit) + ID-formula caption */}
        {level && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {isEdit ? (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select
                  value={level.key_column || ''}
                  displayEmpty
                  onChange={(e) => onLevelPatch?.('key_column', e.target.value)}
                  renderValue={(v) =>
                    v ? (
                      <Typography variant="caption">
                        Key column: <strong>{v as string}</strong>
                      </Typography>
                    ) : (
                      <em>Pick a key column</em>
                    )
                  }
                >
                  {columnNames.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              level.key_column && (
                <Chip
                  label={`Key column: ${level.key_column}`}
                  size="small"
                  variant="outlined"
                />
              )
            )}
            {formulaPhrase && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ID is built by {formulaPhrase}.
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {/* Columns */}
      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Column</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Constraints</TableCell>
              <TableCell>Description</TableCell>
              {isEdit && <TableCell>Always set to</TableCell>}
              <TableCell sx={{ width: 96 }}>Rules</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columnEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={isEdit ? 7 : 6}>
                  <Typography variant="caption" color="text.secondary">
                    No columns on this table.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {columnEntries.map(({ key, column, index }) => (
              <ColumnRowWithCatalog
                key={`${tableName}-col-${index}`}
                column={column}
                isLevelKey={!!level && level.key_column === (column.name || key)}
                mode={mode}
                onColumnPatch={
                  isEdit
                    ? (field, value) => onColumnPatch?.(index, field, value)
                    : undefined
                }
                onOpenRulesEditor={
                  isEdit && onOpenRulesEditor
                    ? () => onOpenRulesEditor(index)
                    : undefined
                }
                onRemoveColumn={isEdit && onRemoveColumn ? () => onRemoveColumn(index) : undefined}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isEdit && (
        <>
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button startIcon={<AddIcon />} size="small" onClick={onAddColumn}>
              Add column
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default LevelTableView;
